import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonToggle,
  ButtonToggleGroup,
  EmptyState,
  Text,
} from '@cubeyou-team/hx-react-components';
import { useSelector } from 'react-redux';

import { DATA_SOURCE_IDS } from '@discover-frontend/constants';
import { InlineResultSingleItem } from '../../..';
import {
  StyledEntityInlineSearchResultsContainer,
  StyledRequestItemContent,
} from './StyledEntityInlineSearch';
import translations from '../../../../lang';
import { InlineSearchItemLoader } from '../../../ContentLoaders';

interface Source {
  id: string;
  label: string;
  disabled?: boolean;
}

interface EntityItem {
  id: string;
  [key: string]: any;
}

interface EntityInlineSearchResultsProps {
  inputValue: string;
  isSearchResultsLoading: boolean;
  results: EntityItem[];
  onEntitySelect: (item: EntityItem) => void;
  onRequireMissingItemRequest?: () => void;
  initialSource?: string;
  sources?: Source[];
  onSourceChange?: (value: string) => void;
  disabledItemSources?: string[];
}

interface RootState {
  queryBuilder: {
    dataSource: string;
  };
}

const EntityInlineSearchResults: FC<EntityInlineSearchResultsProps> = ({
  inputValue,
  isSearchResultsLoading,
  results,
  onEntitySelect,
  onRequireMissingItemRequest = () => {},
  initialSource,
  sources,
  onSourceChange = () => {},
  disabledItemSources,
}) => {
  const dataSource = useSelector(
    (state: RootState) => state.queryBuilder.dataSource
  );
  const [selectedSource, setSelectedSource] = useState<string | undefined>(
    initialSource
  );

  useEffect(() => {
    setSelectedSource(initialSource);
  }, [initialSource]);

  const handleSourceChange = (value: string) => {
    onSourceChange(value);
  };

  const renderRequestBlock = () => (
    <Box borderPosition="top" borderSize="1px" pX="small" pY="small">
      <StyledRequestItemContent>
        <Box pB="medium">
          <Text tag="span" size="small" weight="regular">
            {`Can't find what you're looking for?`}
          </Text>
        </Box>
        <Box>
          <Button
            size="small"
            type="button"
            onClick={onRequireMissingItemRequest}
          >
            Request it now!
          </Button>
        </Box>
      </StyledRequestItemContent>
    </Box>
  );

  const renderToggleSourceButtons = () => (
    <Box pX="small" pY="small" borderSize="2px" borderPosition="bottom">
      <ButtonToggleGroup
        selected={selectedSource}
        onChange={handleSourceChange}
      >
        {sources?.map(source => (
          <ButtonToggle
            key={source.id}
            value={source.id}
            disabled={source.disabled}
          >
            {source.label}
          </ButtonToggle>
        ))}
      </ButtonToggleGroup>
    </Box>
  );

  if (isSearchResultsLoading) {
    return (
      <StyledEntityInlineSearchResultsContainer>
        <InlineSearchItemLoader />
      </StyledEntityInlineSearchResultsContainer>
    );
  }

  if (!inputValue) return null;

  const hasResults = results.length > 0;
  const t = translations['en-US'].QUERY_BUILDER;

  return (
    <Box minHeight={hasResults ? '0' : '200px'}>
      {sources && renderToggleSourceButtons()}

      <StyledEntityInlineSearchResultsContainer>
        <Box pX="small" pY="small" className="inline-search-results-container">
          {hasResults ? (
            <>
              {results.map((item, index) => {
                const isDisabled = Boolean(
                  disabledItemSources?.includes(item.source) ||
                    disabledItemSources?.includes(item.source?.toLowerCase())
                );
                return (
                  <InlineResultSingleItem
                    key={item.id}
                    item={item}
                    tabIndex={index}
                    variant="light"
                    disabled={isDisabled}
                    onItemSelected={onEntitySelect}
                  />
                );
              })}
              {dataSource === DATA_SOURCE_IDS.TWITTER && renderRequestBlock()}
            </>
          ) : (
            <EmptyState
              icon="search"
              size="small"
              mainText={t.emptyItemResult}
              button={
                dataSource === DATA_SOURCE_IDS.TWITTER && (
                  <Button
                    size="small"
                    type="button"
                    onClick={onRequireMissingItemRequest}
                  >
                    Request it now!
                  </Button>
                )
              }
            />
          )}
        </Box>
      </StyledEntityInlineSearchResultsContainer>
    </Box>
  );
};

export default EntityInlineSearchResults;
